// API Configuration
export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://pixelshare-api.local.team-skynet.io';
export const AWS_REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1';
export const COGNITO_USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
export const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// App Constants
export const APP_NAME = 'Pixel Share';
export const APP_VERSION = '1.0.0';
export const APP_DESCRIPTION = 'Professional photo sharing and sales platform';

// Pricing Configuration
export const PRICING = {
  resolutions: {
    web: {
      key: 'web' as const,
      name: 'Web Resolution',
      description: '1080p - Perfect for social media and web use',
      price: 5,
      dimensions: '1920×1080',
    },
    print: {
      key: 'print' as const,
      name: 'Print Resolution',
      description: '300 DPI - High quality for printing',
      price: 15,
      dimensions: '4000×3000',
    },
    full: {
      key: 'full' as const,
      name: 'Full Resolution',
      description: 'Original quality - Highest resolution available',
      price: 25,
      dimensions: 'Original size',
    },
  },
  packages: {
    small: { threshold: 5, discount: 0.1, name: '5+ Photos - 10% off' },
    medium: { threshold: 15, discount: 0.15, name: '15+ Photos - 15% off' },
    large: { threshold: 30, discount: 0.2, name: '30+ Photos - 20% off' },
  },
} as const;

// Subscription Plans (photographer B2B pricing).
// Keyed by canonical backend plan ids (basic/pro/enterprise); no free tier.
// `planId` is the canonical id sent to the API, independent of the object key.
export const SUBSCRIPTION_PLANS = {
  basic: {
    planId: 'basic',
    name: 'Basic',
    monthlyPrice: 29.99,
    annualPrice: 298.90,
    features: {
      events: 5,
      photos: 500,
      storage: '10 GB',
      bandwidth: '50 GB',
      customDomain: false,
      analytics: false,
      apiAccess: false,
      whiteLabel: false,
      support: 'Email',
    },
    cta: 'Subscribe',
    highlighted: false,
  },
  pro: {
    planId: 'pro',
    name: 'Pro',
    monthlyPrice: 59.99,
    annualPrice: 597.90,
    features: {
      events: 25,
      photos: 5000,
      storage: '100 GB',
      bandwidth: '500 GB',
      customDomain: true,
      analytics: true,
      apiAccess: false,
      whiteLabel: false,
      support: 'Priority',
    },
    cta: 'Subscribe',
    highlighted: true,
  },
  enterprise: {
    planId: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 149.99,
    annualPrice: 1497.90,
    features: {
      events: -1,
      photos: -1,
      storage: '1 TB',
      bandwidth: '5 TB',
      customDomain: true,
      analytics: true,
      apiAccess: true,
      whiteLabel: true,
      support: 'Dedicated',
    },
    cta: 'Subscribe',
    highlighted: false,
  },
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'pixelshare_auth_token',
  REFRESH_TOKEN: 'pixelshare_refresh_token',
  USER_DATA: 'pixelshare_user_data',
  CART_DATA: 'pixelshare_cart_data',
  PREFERENCES: 'pixelshare_preferences',
  RECENT_EVENTS: 'pixelshare_recent_events',
} as const;

// UI Constants
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export const PHOTO_GRID_SETTINGS = {
  MIN_COLUMN_WIDTH: 250,
  ITEM_GAP: 16,
  ITEM_HEIGHT_OFFSET: 80, // Height for photo info
  OVERSCAN_ROW_COUNT: 2,
  OVERSCAN_COLUMN_COUNT: 1,
} as const;

// Route Paths
export const ROUTES = {
  HOME: '/',
  EVENTS: '/events',
  EVENT_GALLERY: '/events/:eventId',
  CART: '/cart',
  CHECKOUT: '/checkout',
  LOGIN: '/login',
  REGISTER: '/register',
  ORDER_SUCCESS: '/order/success',
  ORDER_HISTORY: '/orders',
  // Email verification
  VERIFY_EMAIL: '/verify-email',
  // Legal routes
  TERMS_OF_SERVICE: '/terms',
  PRIVACY_POLICY: '/privacy',
  REFUND_POLICY: '/refunds',
  PRICING: '/pricing',
  // Dashboard routes
  DASHBOARD: '/dashboard',
  DASHBOARD_EVENTS: '/dashboard/events',
  DASHBOARD_CUSTOMERS: '/dashboard/customers',
  DASHBOARD_ANALYTICS: '/dashboard/analytics',
  DASHBOARD_SETTINGS: '/dashboard/settings',
  DASHBOARD_ACCOUNT: '/dashboard/settings/account',
  DASHBOARD_BILLING: '/dashboard/billing',
  // Admin routes
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_EVENTS: '/admin/events',
  ADMIN_ORDERS: '/admin/orders',
  ADMIN_CUSTOMERS: '/admin/customers',
  ADMIN_SHOWCASE: '/admin/showcase',
  ADMIN_TENANTS: '/admin/tenants',
  ADMIN_USERS: '/admin/users',
  ADMIN_SYSTEM_LOGS: '/admin/system-logs',
  ADMIN_AUDIT_LOGS: '/admin/audit-logs',
} as const;

// Event Status Options
export const EVENT_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;

export const EVENT_STATUS_LABELS = {
  [EVENT_STATUS.DRAFT]: 'Draft',
  [EVENT_STATUS.PUBLISHED]: 'Published',
  [EVENT_STATUS.ARCHIVED]: 'Archived',
} as const;

// Order Status Options
export const ORDER_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export const ORDER_STATUS_LABELS = {
  [ORDER_STATUS.PENDING]: 'Pending',
  [ORDER_STATUS.PAID]: 'Paid',
  [ORDER_STATUS.PROCESSING]: 'Processing',
  [ORDER_STATUS.COMPLETED]: 'Completed',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',
} as const;

// User Roles
export const USER_ROLES = {
  CUSTOMER: 'customer',
  PHOTOGRAPHER: 'photographer',
  ADMIN: 'admin',
} as const;

// API Endpoints - routes match API Gateway paths (no /api/v1 prefix)
export const API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: '/auth/login',
  AUTH_REGISTER: '/auth/register',
  AUTH_REFRESH: '/auth/refresh',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_PROFILE: '/auth/me',
  // Magic Link
  AUTH_MAGIC_LINK_REQUEST: '/auth/magic-link/request',
  AUTH_MAGIC_LINK_VERIFY: '/auth/magic-link/verify',
  // Passwordless Registration
  AUTH_REGISTER_PASSWORDLESS: '/auth/register/passwordless',
  // Email Verification
  AUTH_VERIFY_EMAIL: '/auth/verify-email',
  AUTH_RESEND_VERIFICATION: '/auth/resend-verification',
  // OAuth
  AUTH_OAUTH_PROVIDERS: '/auth/oauth/providers',
  AUTH_OAUTH_GOOGLE: '/auth/oauth/google',
  AUTH_OAUTH_GITHUB: '/auth/oauth/github',
  AUTH_OAUTH_EXCHANGE: '/auth/oauth/exchange',
  // Two-Factor Authentication
  AUTH_2FA_SETUP: '/auth/2fa/setup',
  AUTH_2FA_VERIFY: '/auth/2fa/verify',
  AUTH_2FA_DISABLE: '/auth/2fa/disable',
  AUTH_2FA_STATUS: '/auth/2fa/status',
  AUTH_2FA_BACKUP_CODES: '/auth/2fa/backup-codes/regenerate',
  // GDPR Data Export
  AUTH_DATA_EXPORT: '/auth/data-export',

  // Events
  EVENTS: '/events',
  EVENT_BY_ID: (id: string) => `/events/${id}`,
  EVENT_PHOTOS: (id: string) => `/events/${id}/photos`,

  // Photos
  PHOTOS: '/photos',
  PHOTO_BY_ID: (id: string) => `/photos/${id}`,

  // Orders
  ORDERS: '/orders',
  ORDER_BY_ID: (id: string) => `/orders/${id}`,
  CREATE_PAYMENT_INTENT: '/orders/payment-intent',

  // Cart
  VALIDATE_DISCOUNT: '/cart/validate-discount',

  // Admin
  ADMIN_STATS: '/admin/stats',
  ADMIN_EVENTS: '/admin/events',
  ADMIN_ORDERS: '/admin/orders',
  ADMIN_CUSTOMERS: '/admin/customers',

  // Pricing Configuration
  PRICING_CONFIG: '/pricing',
  PRICING_BASE: '/pricing/base',
  PRICING_DISCOUNTS: '/pricing/discounts',
  PRICING_MULTIPLIERS: '/pricing/multipliers',
  PRICING_OFFERS: '/pricing/offers',
  PRICING_COMMISSION: '/pricing/commission',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
  PAYMENT: 'Payment processing failed. Please try again.',
  FILE_UPLOAD: 'File upload failed. Please try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Successfully logged in!',
  REGISTER: 'Account created successfully!',
  LOGOUT: 'Successfully logged out!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  ORDER_PLACED: 'Order placed successfully!',
  DISCOUNT_APPLIED: 'Discount code applied!',
  ITEM_ADDED_TO_CART: 'Item added to cart!',
  ITEM_REMOVED_FROM_CART: 'Item removed from cart!',
} as const;

// Validation Rules
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 50,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_PATTERN: /^\+?[\d\s\-()]+$/,
} as const;

// Image Settings
export const IMAGE_SETTINGS = {
  PLACEHOLDER: '/placeholder-image.jpg',
  THUMBNAIL_SIZE: 300,
  WEB_SIZE: 1920,
  PRINT_SIZE: 4000,
  SUPPORTED_FORMATS: ['jpg', 'jpeg', 'png', 'webp'],
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
} as const;

// Performance Settings
export const PERFORMANCE = {
  DEBOUNCE_DELAY: 300,
  CACHE_TTL: 5 * 60 * 1000, // 5 minutes
  RETRY_ATTEMPTS: 3,
  TIMEOUT: 30 * 1000, // 30 seconds
  INTERSECTION_THRESHOLD: 0.1,
  LAZY_LOAD_OFFSET: 100,
} as const;
